import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { Col, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { toSafeInteger } from 'lodash';
import { useDebounce } from 'use-debounce';
import { useHistory, useLocation } from 'react-router-dom';

import {
  ANNUALIZED_MFR_GMV,
  ANNUALIZED_MFR_GMV_UNSTRUCTURED,
  ANNUALIZED_MFR_GMV_UNBRANDED_PRODUCT,
  ANNUALIZED_MFR_GMV_BRANDS,
  ANNUALIZED_MFR_GMV_MANUFACTURERS,
  ANNUALIZED_MFR_GMV_PRODUCTS,
  ANNUALIZED_MFR_GMV_DISTRIBUTORS,
} from 'lib/networking/endpoints';
import { backgroundDarkGrey, backgroundGrey5 } from 'lib/css/colors';
import CodifyDBCatalogView from 'components/codify_db/CodifyDBCatalogView';
import Container from 'components/shared/Container';
import { DEFAULT_UPB_PAGE_SIZE, PACKER_BRAND_ID } from 'lib/constants';
import DistributorModal from 'components/codify_db/DistributorModal';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PostHogEvents from 'lib/enums/PostHogEvents';
import PaginationBar from 'components/shared/PaginationBar';
import { ProductType } from 'lib/enums/ProductType';
import SearchBar from 'components/shared/SearchBar';
import { SEARCH_PLACEHOLDERS, VIEW_MODES } from 'components/codify_db/constants';
import UPBGMVCard from 'components/codify_db/UPBGMVCard';
import useGet from 'lib/hooks/useGet';
import usePostHog from 'lib/hooks/usePostHog';
import { UPBViewContext } from 'components/upb_viewer/UPBViewContainer';

const DEFAULT_ALL_CATEGORY_OPTION = { label: 'All', value: null };

function CodifyDBView() {
  const history = useHistory();
  const location = useLocation();

  // Parse URL search params manually for compatibility
  const searchParams = new URLSearchParams(location.search);
  const manufacturerId = searchParams.get('manufacturer_id');
  const brandId = searchParams.get('brand_id');

  // Separate page states for each view
  const [manufacturersPage, setManufacturersPage] = useState(1);
  const [brandsPage, setBrandsPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebounce(searchQuery, 600);
  const [productsToShow, setProductsToShow] = useState([]);
  const { viewGMV } = useContext(UPBViewContext);

  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [manufacturersToShow, setManufacturersToShow] = useState([]);
  const [brandsToShow, setBrandsToShow] = useState([]);

  const posthog = usePostHog();

  const [showDistributorModal, setShowDistributorModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [selectedDistributorItem, setSelectedDistributorItem] = useState(null);

  // Determine current view mode based on URL parameters
  const viewMode = useMemo(() => {
    if (brandId && manufacturerId) return VIEW_MODES.PRODUCTS;
    if (manufacturerId) return VIEW_MODES.BRANDS;
    return VIEW_MODES.MANUFACTURERS;
  }, [manufacturerId, brandId]);

  // Helper function to update URL search params
  const updateUrlParams = useCallback(
    params => {
      const newSearchParams = new URLSearchParams(location.search);

      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          newSearchParams.set(key, value);
        } else {
          newSearchParams.delete(key);
        }
      });

      const newSearch = newSearchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      history.push(newUrl);
    },
    [location.pathname, location.search, history],
  );

  // Calculate current page and offset based on view mode
  const currentPage = useMemo(() => {
    switch (viewMode) {
      case VIEW_MODES.MANUFACTURERS:
        return manufacturersPage;
      case VIEW_MODES.BRANDS:
        return brandsPage;
      case VIEW_MODES.PRODUCTS:
        return productsPage;
      default:
        return 1;
    }
  }, [viewMode, manufacturersPage, brandsPage, productsPage]);

  const page = useMemo(() => Math.max(toSafeInteger(currentPage) - 1, 0), [currentPage]);
  const offset = useMemo(() => {
    return page * DEFAULT_UPB_PAGE_SIZE || 0;
  }, [page]);

  // Update page setter based on view mode
  const setCurrentPage = useCallback(
    newPage => {
      switch (viewMode) {
        case VIEW_MODES.MANUFACTURERS:
          setManufacturersPage(newPage);
          break;
        case VIEW_MODES.BRANDS:
          setBrandsPage(newPage);
          break;
        case VIEW_MODES.PRODUCTS:
          setProductsPage(newPage);
          break;
      }
    },
    [viewMode],
  );

  // Reset current view's page when search query changes
  useEffect(() => {
    switch (viewMode) {
      case VIEW_MODES.MANUFACTURERS:
        setManufacturersPage(1);
        break;
      case VIEW_MODES.BRANDS:
        setBrandsPage(1);
        break;
      case VIEW_MODES.PRODUCTS:
        setProductsPage(1);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]); // Intentionally not including viewMode to avoid resetting on view change

  // Fetch manufacturers with GMV ordering
  const {
    data: { manufacturers = [], count: manufacturersCount = 0 } = {},
    loading: manufacturersLoading,
    refetch: refetchManufacturers,
  } = useGet(ANNUALIZED_MFR_GMV_MANUFACTURERS, {
    limit: DEFAULT_UPB_PAGE_SIZE,
    offset: viewMode === VIEW_MODES.MANUFACTURERS ? offset : 0,
    query: viewMode === VIEW_MODES.MANUFACTURERS ? debouncedQuery : '',
  });

  // Fetch brands when manufacturer is selected
  const { data: { brands = [], count: brandsCount = 0 } = {}, loading: brandsLoading } = useGet(
    ANNUALIZED_MFR_GMV_BRANDS,
    {
      search: viewMode === VIEW_MODES.BRANDS ? debouncedQuery : '',
      manufacturer_id: manufacturerId || '',
      limit: DEFAULT_UPB_PAGE_SIZE,
      offset: viewMode === VIEW_MODES.BRANDS ? offset : 0,
    },
    !manufacturerId || (viewMode !== VIEW_MODES.BRANDS && viewMode !== VIEW_MODES.PRODUCTS),
  );

  const { data: totalGMVs = {}, loading: GMVDataLoading } = useGet(
    ANNUALIZED_MFR_GMV,
    {
      mfr_ids: manufacturerId ? [parseInt(manufacturerId, 10)] : [],
      brand_ids: brandId ? [parseInt(brandId, 10)] : [],
    },
    !viewGMV || searchQuery,
  );

  // Fetch Packer Brand GMV - only when viewing manufacturers
  const { data: packerBrandGMVs = {}, loading: packerBrandGMVLoading } = useGet(
    ANNUALIZED_MFR_GMV,
    {
      mfr_ids: [],
      brand_ids: [PACKER_BRAND_ID],
    },
    viewMode !== VIEW_MODES.MANUFACTURERS || !viewGMV,
  );

  // Fetch Unstructured GMV - only when viewing manufacturers
  const { data: unstructuredGMVs = {}, loading: unstructuredGMVLoading } = useGet(
    ANNUALIZED_MFR_GMV_UNSTRUCTURED,
    {},
    viewMode !== VIEW_MODES.MANUFACTURERS || !viewGMV,
  );

  // Fetch Unbrand Product GMV - only when viewing manufacturers
  const { data: unbrandedProductGMVs = {}, loading: unbrandedProductGMVLoading } = useGet(
    ANNUALIZED_MFR_GMV_UNBRANDED_PRODUCT,
    {},
    viewMode !== VIEW_MODES.MANUFACTURERS || !viewGMV,
  );

  // Add useGet for distributors - only fetch when we have a selected item
  const {
    data: { distributors = [], total_count: distributorsCount = 0 } = {},
    loading: distributorsLoading,
    refetch: refetchDistributors,
  } = useGet(
    ANNUALIZED_MFR_GMV_DISTRIBUTORS,
    {
      ...(selectedDistributorItem?.type === VIEW_MODES.MANUFACTURERS && {
        manufacturer_id: selectedDistributorItem.id,
      }),
      ...(selectedDistributorItem?.type === VIEW_MODES.BRANDS && {
        brand_id: selectedDistributorItem.id,
      }),
    },
    !selectedDistributorItem, // skip if no item selected
  );

  useEffect(() => {
    posthog.capture(PostHogEvents.UPB_PAGE_VIEWED);
  }, [posthog]);

  // Find selected manufacturer and brand objects
  useEffect(() => {
    if (manufacturerId && manufacturers.length) {
      const manufacturer = manufacturers.find(m => m.id.toString() === manufacturerId);
      if (manufacturer) {
        setSelectedManufacturer(manufacturer);
      }
      // Don't set to null if manufacturer not found - keep the existing one
    } else if (!manufacturerId) {
      // Only clear when there's no manufacturerId (back to manufacturers view)
      setSelectedManufacturer(null);
    }
  }, [manufacturerId, manufacturers]);

  useEffect(() => {
    if (brandId && brands.length) {
      const brand = brands.find(b => b.id.toString() === brandId);
      if (brand) {
        setSelectedBrand(brand);
      }
      // Don't set to null if brand not found - keep the existing one
    } else if (!brandId) {
      // Only clear when there's no brandId
      setSelectedBrand(null);
    }
  }, [brandId, brands]);

  const onSearchQueryChange = query => {
    setSearchQuery(query);
    setProductsToShow([]);
    setManufacturersToShow([]);
    setBrandsToShow([]);
    setCurrentPage(1);
  };

  // Fetch products when brand is selected - using Snowflake instead of OpenSearch
  const {
    data: { data: products = [], count: productsCount = 0 } = {},
    loading: productsLoading,
    error,
  } = useGet(
    ANNUALIZED_MFR_GMV_PRODUCTS,
    {
      brand_id: brandId,
      limit: DEFAULT_UPB_PAGE_SIZE,
      offset: viewMode === VIEW_MODES.PRODUCTS ? offset : 0,
      search_query: viewMode === VIEW_MODES.PRODUCTS ? debouncedQuery : '',
    },
    viewMode !== VIEW_MODES.PRODUCTS || !brandId,
  );

  useEffect(() => {
    if (viewMode === VIEW_MODES.MANUFACTURERS && manufacturers.length) {
      setManufacturersToShow(manufacturers);
    }
  }, [manufacturers, viewMode]);

  useEffect(() => {
    if (viewMode === VIEW_MODES.BRANDS && brands.length) {
      setBrandsToShow(brands);
    }
  }, [brands, viewMode]);

  useEffect(() => {
    if (viewMode === VIEW_MODES.PRODUCTS && products.length) {
      setProductsToShow(products);
    }
  }, [products, viewMode]);

  const handleManufacturerClick = manufacturer => {
    setBrandsPage(1); // Reset brands page when selecting new manufacturer
    // Update URL to show brands for this manufacturer
    updateUrlParams({
      manufacturer_id: manufacturer.id,
      brand_id: null, // Remove brand_id when selecting new manufacturer
    });
    setSearchQuery('');
  };

  const handleBrandClick = brand => {
    setProductsPage(1); // Reset products page when selecting new brand
    // Update URL to show products for this brand
    updateUrlParams({
      manufacturer_id: manufacturerId, // Keep manufacturer_id
      brand_id: brand.id,
    });
    setSearchQuery('');
  };

  const handleBackClick = () => {
    if (viewMode === VIEW_MODES.PRODUCTS) {
      // Go back to brands (remove brand_id, keep manufacturer_id)
      // Don't reset brandsPage to maintain position
      updateUrlParams({
        brand_id: null,
      });
    } else if (viewMode === VIEW_MODES.BRANDS) {
      // Go back to manufacturers (remove manufacturer_id)
      // Don't reset manufacturersPage to maintain position
      updateUrlParams({
        manufacturer_id: null,
      });
    }
    setSearchQuery('');
  };

  const handleDistributorClick = useCallback(
    async item => {
      const isManufacturer = viewMode === VIEW_MODES.MANUFACTURERS;

      setSelectedDistributorItem({
        ...item,
        type: isManufacturer ? VIEW_MODES.MANUFACTURERS : VIEW_MODES.BRANDS,
      });
      setModalTitle(isManufacturer ? `Manufacturer: ${item.name}` : `Brand: ${item.name}`);
      setShowDistributorModal(true);
    },
    [viewMode],
  );

  const handleCloseDistributorModal = useCallback(() => {
    setShowDistributorModal(false);
    setSelectedDistributorItem(null);
  }, []);

  const isSearchPending = searchQuery !== debouncedQuery;
  const loading = manufacturersLoading || brandsLoading || productsLoading || isSearchPending;

  const getItemCount = useCallback(() => {
    switch (viewMode) {
      case VIEW_MODES.MANUFACTURERS:
        return manufacturersCount;
      case VIEW_MODES.BRANDS:
        return brandsCount;
      case VIEW_MODES.PRODUCTS:
        return productsCount;
      default:
        return 0;
    }
  }, [viewMode, manufacturersCount, brandsCount, productsCount]);

  const totalPages = useMemo(() => {
    const itemCount = getItemCount();
    return Math.ceil(itemCount / DEFAULT_UPB_PAGE_SIZE);
  }, [getItemCount]);

  const getSearchPlaceholder = () => {
    return SEARCH_PLACEHOLDERS[viewMode] || 'Search...';
  };

  if (error) {
    return null;
  }

  const totalInvoicePlusOrderingGMV = searchQuery
    ? 0
    : totalGMVs?.total_invoice_plus_ordering_gmv || 0;

  const packerBrandGMV = packerBrandGMVs?.total_invoice_plus_ordering_gmv || 0;
  const unstructuredGMV = unstructuredGMVs?.total_invoice_plus_ordering_gmv || 0;
  const unbrandedProductGMV = unbrandedProductGMVs?.total_invoice_plus_ordering_gmv || 0;

  const currentItemCount = getItemCount();

  return (
    <Container className={css(styles.container)}>
      <Row className={classNames('mb-1 ml-0', css(styles.titleContainer))}>
        <Col sm="2">
          <div className="d-flex align-items-center pl-2">
            <div>
              <h4 className="font-weight-bold">UPB Mfr Viewer</h4>
            </div>
          </div>
        </Col>
        <Col sm="6" className={css(styles.headerGMVContainer)}>
          <UPBGMVCard
            show={viewGMV}
            viewMode={viewMode}
            selectedManufacturer={selectedManufacturer}
            selectedBrand={selectedBrand}
            totalGMV={totalInvoicePlusOrderingGMV}
            totalGMVLoading={GMVDataLoading}
            packerBrandGMV={packerBrandGMV}
            packerBrandGMVLoading={packerBrandGMVLoading}
            unstructuredGMV={unstructuredGMV}
            unstructuredGMVLoading={unstructuredGMVLoading}
            unbrandedProductGMV={unbrandedProductGMV}
            unbrandedProductGMVLoading={unbrandedProductGMVLoading}
          />
        </Col>
        <Col sm="4">
          <div className="d-flex justify-content-end flex-row align-items-center pr-2">
            <div className="w-50 text-right mr-2 text-muted">
              {loading ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : currentItemCount > 0 ? (
                <span>
                  {`${offset + 1}-${Math.min(
                    offset + DEFAULT_UPB_PAGE_SIZE,
                    currentItemCount,
                  )} of ${currentItemCount} `}
                  {viewMode}
                </span>
              ) : null}
            </div>
            <div className={css(styles.searchBarWrapper)}>
              <SearchBar
                value={searchQuery}
                setValue={onSearchQueryChange}
                placeholder={getSearchPlaceholder()}
              />
            </div>
          </div>
        </Col>
      </Row>
      <Row className={css(styles.catalogContainer)}>
        <Col md={12} className={classNames(css(styles.productSection), 'mt-3')}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="d-flex justify-content-center flex-column align-items-center">
              <CodifyDBCatalogView
                viewMode={viewMode}
                manufacturers={manufacturersToShow}
                brands={brandsToShow}
                products={productsToShow}
                productCount={productsCount}
                productType={ProductType.MANUFACTURER}
                selectedManufacturer={selectedManufacturer}
                selectedBrand={selectedBrand}
                onManufacturerClick={handleManufacturerClick}
                onBrandClick={handleBrandClick}
                onBackClick={handleBackClick}
                onDistributorClick={handleDistributorClick}
              />
              {totalPages > 1 && (
                <div className="my-4">
                  <PaginationBar
                    totalPages={totalPages}
                    currentPageNumber={page}
                    onPageChange={newPage => setCurrentPage(newPage + 1)}
                  />
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
      <DistributorModal
        show={showDistributorModal}
        onHide={handleCloseDistributorModal}
        distributors={distributors}
        loading={distributorsLoading}
        title={modalTitle}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  titleContainer: {
    top: 50,
    backgroundColor: backgroundDarkGrey,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0px 40px 0px 20px',
    minHeight: '110px',
  },
  catalogContainer: {
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 150px)',
  },
  productSection: {
    background: backgroundGrey5,
    position: 'sticky',
    top: 50,
    display: 'flex',
  },
  headerGMVContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '8px',
  },
  searchBarWrapper: {
    maxWidth: '320px',
    minWidth: '280px',
  },
});

export { DEFAULT_ALL_CATEGORY_OPTION };
export default CodifyDBView;
